<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="bank-accounts-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Bank Accounts</h1>
          <p class="page-subtitle">Manage your organisation's bank and cash accounts</p>
        </div>
        <div class="header-actions">
          <IntercomButton variant="primary" @click="openAddModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Add Account</span>
          </IntercomButton>
        </div>
      </div>

      <!-- Balance Summary Cards -->
      <div v-if="accountsWithBalances.length > 0" class="summary-section">
        <div class="summary-cards">
          <IntercomCard
            v-for="account in accountsWithBalances"
            :key="account.id"
            class="balance-card"
            :class="`balance-card--${account.account_type}`"
          >
            <div class="balance-card-header">
              <span class="balance-card-name">{{ account.name }}</span>
              <IntercomBadge :variant="typeBadgeVariant(account.account_type)">
                {{ formatAccountType(account.account_type) }}
              </IntercomBadge>
            </div>
            <div class="balance-card-amount">
              {{ formatCompactCurrency(account.current_balance || 0) }}
            </div>
            <div v-if="account.bank_name" class="balance-card-bank">
              {{ account.bank_name }}
              <span v-if="account.bank_account_number"> &middot; {{ account.bank_account_number }}</span>
            </div>
          </IntercomCard>

          <!-- Total Card -->
          <IntercomCard class="balance-card balance-card--total">
            <div class="balance-card-header">
              <span class="balance-card-name">Total Balance</span>
              <IntercomBadge variant="success">All Accounts</IntercomBadge>
            </div>
            <div class="balance-card-amount balance-card-amount--total">
              {{ formatCompactCurrency(totalBalance) }}
            </div>
            <div class="balance-card-bank">{{ activeAccountCount }} active account{{ activeAccountCount !== 1 ? 's' : '' }}</div>
          </IntercomCard>
        </div>
      </div>

      <!-- Accounts Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="accountsWithBalances"
          :loading="isLoading"
          hoverable
        >
          <template #cell-account_type="{ value }">
            <IntercomBadge :variant="typeBadgeVariant(value)">
              {{ formatAccountType(value) }}
            </IntercomBadge>
          </template>
          <template #cell-opening_balance="{ value }">
            {{ formatFullCurrency(value || 0) }}
          </template>
          <template #cell-current_balance="{ value }">
            {{ formatFullCurrency(value || 0) }}
          </template>
          <template #cell-is_active="{ row }">
            <div class="status-cell">
              <IntercomBadge :variant="row.is_active ? 'success' : 'default'">
                {{ row.is_active ? 'Active' : 'Inactive' }}
              </IntercomBadge>
              <button class="toggle-btn" @click.stop="openEditModal(row)">Edit</button>
              <button
                class="toggle-btn"
                :disabled="isTogglingId === row.id"
                @click.stop="toggleActive(row)"
              >
                {{ row.is_active ? 'Deactivate' : 'Activate' }}
              </button>
            </div>
          </template>
        </IntercomTable>
        <p v-if="!isLoading && accountsWithBalances.length === 0" class="empty-text">
          No bank accounts found. Add your first account to get started.
        </p>
      </IntercomCard>

      <!-- Add/Edit Account Modal -->
      <IntercomModal v-model:show="showAddModal" :title="editingAccount ? 'Edit Bank Account' : 'Add Bank Account'">
        <form class="account-form" @submit.prevent="editingAccount ? handleUpdate() : handleCreate()">
          <div class="form-field">
            <label class="form-label">Account Name <span class="required">*</span></label>
            <IntercomInput
              v-model="form.name"
              placeholder="e.g. Main Savings Account"
              :disabled="isSaving"
            />
          </div>

          <div class="form-field">
            <label class="form-label">Account Type <span class="required">*</span></label>
            <IntercomSelect
              v-model="form.account_type"
              :options="accountTypeOptions"
              placeholder="Select account type"
              :disabled="isSaving"
            />
          </div>

          <template v-if="showBankFields">
            <div class="form-field">
              <label class="form-label">Bank Name</label>
              <IntercomInput
                v-model="form.bank_name"
                placeholder="e.g. State Bank of India"
                :disabled="isSaving"
              />
            </div>

            <div class="form-field">
              <label class="form-label">Bank Account Number</label>
              <IntercomInput
                v-model="form.bank_account_number"
                placeholder="e.g. 1234567890123"
                maxlength="30"
                :disabled="isSaving"
              />
            </div>
          </template>

          <div class="form-field">
            <label class="form-label">Opening Balance (Rs)</label>
            <IntercomInput
              v-model="form.opening_balance"
              type="number"
              placeholder="0.00"
              :disabled="isSaving"
            />
          </div>

          <div class="form-field">
            <IntercomDateInput
              v-model="form.opening_balance_date"
              label="Opening Balance Date"
              :disabled="isSaving"
            />
          </div>

          <div class="form-actions">
            <IntercomButton variant="secondary" type="button" :disabled="isSaving" @click="closeModal">
              Cancel
            </IntercomButton>
            <IntercomButton variant="primary" type="submit" :loading="isSaving" :disabled="!isFormValid || isSaving">
              {{ editingAccount ? 'Save Changes' : 'Add Account' }}
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency, formatFullCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listBankAccounts, getAccountBalances, createBankAccount, updateBankAccount } = useSeva()
const { navSections } = useNavigation()

// State
const accounts = ref([])
const balances = ref({})
const isLoading = ref(false)
const isSaving = ref(false)
const isTogglingId = ref(null)
const showAddModal = ref(false)
const editingAccount = ref(null)

const emptyForm = () => ({
  name: '',
  account_type: null,
  bank_name: '',
  bank_account_number: '',
  opening_balance: '',
  opening_balance_date: dayjs().format('YYYY-MM-DD'),
})

const form = ref(emptyForm())

// Computed
const accountTypeOptions = [
  { value: 'savings', label: 'Savings' },
  { value: 'current', label: 'Current' },
  { value: 'cash', label: 'Cash' },
  { value: 'fd', label: 'Fixed Deposit (FD)' },
  { value: 'fcra', label: 'FCRA Account' },
]

const showBankFields = computed(() =>
  form.value.account_type && form.value.account_type !== 'cash'
)

const isFormValid = computed(() =>
  form.value.name.trim() !== '' && form.value.account_type !== null
)

const accountsWithBalances = computed(() =>
  accounts.value.map(account => ({
    ...account,
    current_balance: balances.value[account.id] ?? account.opening_balance ?? 0,
  }))
)

const totalBalance = computed(() =>
  accountsWithBalances.value
    .filter(a => a.is_active)
    .reduce((sum, a) => sum + (a.current_balance || 0), 0)
)

const activeAccountCount = computed(() =>
  accountsWithBalances.value.filter(a => a.is_active).length
)

const columns = [
  { key: 'bank_account_number', title: 'Account No.', width: '130px' },
  { key: 'name', title: 'Name', width: '200px' },
  { key: 'account_type', title: 'Type', width: '130px' },
  { key: 'bank_name', title: 'Bank Name', width: '180px' },
  { key: 'opening_balance', title: 'Opening Balance', width: '160px', align: 'right' },
  { key: 'current_balance', title: 'Current Balance', width: '160px', align: 'right' },
  { key: 'is_active', title: 'Status', width: '160px' },
]

// Helpers
function formatAccountType(type) {
  const map = {
    savings: 'Savings',
    current: 'Current',
    cash: 'Cash',
    fd: 'FD',
    fcra: 'FCRA',
  }
  return map[type] || type
}

function typeBadgeVariant(type) {
  const map = {
    savings: 'info',
    current: 'info',
    cash: 'success',
    fd: 'warning',
    fcra: 'default',
  }
  return map[type] || 'default'
}

// Data fetching
const fetchAccounts = async () => {
  isLoading.value = true
  try {
    const [accountsRes, balancesRes] = await Promise.all([
      listBankAccounts(),
      getAccountBalances(),
    ])
    accounts.value = accountsRes?.accounts || []
    const rawBalances = balancesRes?.balances || []
    balances.value = {}
    for (const b of rawBalances) {
      balances.value[b.account_id] = b.current_balance
    }
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
    toast.error('Failed to load bank accounts')
  } finally {
    isLoading.value = false
  }
}

// Actions
const openAddModal = () => {
  editingAccount.value = null
  form.value = emptyForm()
  showAddModal.value = true
}

const openEditModal = (account) => {
  editingAccount.value = account
  form.value = {
    name: account.name || '',
    account_type: account.account_type || null,
    bank_name: account.bank_name || '',
    bank_account_number: account.bank_account_number || '',
    opening_balance: account.opening_balance || '',
    opening_balance_date: account.opening_balance_date
      ? dayjs(account.opening_balance_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD'),
  }
  showAddModal.value = true
}

const closeModal = () => {
  showAddModal.value = false
  editingAccount.value = null
  form.value = emptyForm()
}

const handleCreate = async () => {
  if (!isFormValid.value) return
  isSaving.value = true
  try {
    const payload = {
      name: form.value.name.trim(),
      account_type: form.value.account_type,
      opening_balance: parseFloat(form.value.opening_balance) || 0,
      opening_balance_date: form.value.opening_balance_date,
    }
    if (showBankFields.value) {
      if (form.value.bank_name.trim()) payload.bank_name = form.value.bank_name.trim()
      if (form.value.bank_account_number.trim()) payload.bank_account_number = form.value.bank_account_number.trim()
    }
    await createBankAccount(payload)
    toast.success('Bank account added successfully')
    closeModal()
    fetchAccounts()
  } catch (error) {
    console.error('Failed to create bank account:', error)
    toast.error(error.detail || error.message || 'Failed to add bank account')
  } finally {
    isSaving.value = false
  }
}

const handleUpdate = async () => {
  if (!isFormValid.value || !editingAccount.value) return
  isSaving.value = true
  try {
    const payload = {
      name: form.value.name.trim(),
      account_type: form.value.account_type,
    }
    if (showBankFields.value) {
      payload.bank_name = form.value.bank_name.trim() || null
      payload.bank_account_number = form.value.bank_account_number.trim() || null
    }
    await updateBankAccount(editingAccount.value.id, payload)
    toast.success('Bank account updated')
    closeModal()
    fetchAccounts()
  } catch (error) {
    console.error('Failed to update bank account:', error)
    toast.error(error.detail || error.message || 'Failed to update account')
  } finally {
    isSaving.value = false
  }
}

const toggleActive = async (account) => {
  isTogglingId.value = account.id
  try {
    await updateBankAccount(account.id, { is_active: !account.is_active })
    toast.success(account.is_active ? 'Account deactivated' : 'Account activated')
    fetchAccounts()
  } catch (error) {
    console.error('Failed to toggle account status:', error)
    toast.error(error.detail || error.message || 'Failed to update account')
  } finally {
    isTogglingId.value = null
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchAccounts()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.bank-accounts-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

/* Summary Cards */
.summary-section {
  margin-bottom: var(--space-6);
}

.summary-cards {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.balance-card {
  flex: 1;
  min-width: 200px;
  max-width: 280px;
  border-top: 3px solid var(--color-gray-200);
}

.balance-card--savings,
.balance-card--current {
  border-top-color: var(--color-blue-500, #3b82f6);
}

.balance-card--cash {
  border-top-color: var(--color-green-500, #22c55e);
}

.balance-card--fd {
  border-top-color: var(--color-purple-500, #a855f7);
}

.balance-card--fcra {
  border-top-color: var(--color-orange-500, #f97316);
}

.balance-card--total {
  border-top-color: var(--color-charcoal, #1f2937);
}

.balance-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-2);
  gap: var(--space-2);
}

.balance-card-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.balance-card-amount {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin-bottom: var(--space-1);
}

.balance-card-amount--total {
  font-size: var(--font-size-2xl);
}

.balance-card-bank {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

/* Table */
.status-cell {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.toggle-btn {
  background: none;
  border: none;
  font-size: var(--font-size-xs);
  color: var(--color-blue-600, #2563eb);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  white-space: nowrap;
}

.toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Form */
.account-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
}

.required {
  color: var(--color-red-500, #ef4444);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

/* Responsive */
@media (max-width: 768px) {
  .bank-accounts-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .header-actions {
    width: 100%;
  }

  .summary-cards {
    flex-direction: column;
  }

  .balance-card {
    max-width: none;
  }
}
</style>
